import React, { FC, useCallback, useEffect, useMemo } from 'react';

import Measure from 'react-measure';
import styled from 'styled-components';

import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useUIContext } from 'state/ui';

import { BottomServiceModeView } from './bottom-service-mode.view';
import { useBottomServiceModeDetails } from './hooks/use-bottom-service-details';
import { useRoutesDisplayBottomService } from './hooks/use-routes-display-bottom-service';
import { IBottomServiceModeProps } from './types';

const StickyBottomServiceMode = styled(BottomServiceModeView)<{ $bottomInset: number }>`
  position: fixed;
  bottom: ${p => p.$bottomInset}px;
  z-index: ${Styles.zIndex.overlay};
  width: 100%;
  ${Styles.mobile`
    bottom: calc(${(p: { $bottomInset: number }) =>
      p.$bottomInset}px + env(safe-area-inset-bottom));
  `}
`;

/**
 *
 * BottomServiceMode purpose
 *
 */
const BottomServiceMode: FC<IBottomServiceModeProps> = () => {
  const enableBottomServiceMode = useFlag(LaunchDarklyFlag.ENABLE_BOTTOM_SERVICE_MODE);
  const { appFooterDimensions, updateBottomServiceDimensions } = useUIContext();
  const bottomServiceModeDetails = useBottomServiceModeDetails();
  const { CancellationModal } = bottomServiceModeDetails;
  const showBottomServiceOnRoutes = useRoutesDisplayBottomService();
  const isDisplayBottomServiceMode = useMemo(
    () => enableBottomServiceMode && showBottomServiceOnRoutes,
    [enableBottomServiceMode, showBottomServiceOnRoutes]
  );
  const handleResize = useCallback(
    contentRect => {
      if (contentRect.bounds) {
        updateBottomServiceDimensions({
          top: contentRect.bounds.top,
          left: contentRect.bounds.left,
          width: contentRect.bounds.width,
          height: contentRect.bounds.height
        });
      }
    },
    [updateBottomServiceDimensions]
  );
  useEffect(() => {
    if (!isDisplayBottomServiceMode) {
      updateBottomServiceDimensions({
        top: 0,
        left: 0,
        width: 0,
        height: 0
      });
    }
  }, [isDisplayBottomServiceMode, updateBottomServiceDimensions]);
  if (!isDisplayBottomServiceMode) {
    return null;
  }
  return (
    <>
      <Measure bounds onResize={handleResize}>
        {({ measureRef }) => (
          <StickyBottomServiceMode
            ref={measureRef}
            $bottomInset={appFooterDimensions.height}
            {...bottomServiceModeDetails}
          />
        )}
      </Measure>
      <CancellationModal />
    </>
  );
};

export default BottomServiceMode;
